import { Controller } from '@hotwired/stimulus';
import { dashboardAction, dashboardGetCurrentViewKey, getMeta } from '../helpers';

export default class extends Controller {
    static targets = [
        'table',
        'column',
        'visibilityInput',
        'visibilityInputAll',
        'currencySelect',
        'filterTemplateSelect',
        'bulkCheckbox',
        'bulkCheckboxAll',
        'bulkActionDropdown',
        'bulkActionLink',
        'bulkCountBadge',
    ];

    initialize() {
        super.initialize();
        this.scrollHandled = true;
        this.perPageTypeTimer = null;
        this.totalBulkCount = 0;
        this.componentKey = `${dashboardGetCurrentViewKey()}_${this.element.dataset.modelClass}`;
        this.listenForVerticalScroll = (event) => this._listenForVerticalScroll(event);
        this.listenForBulkSuccess = (event) => this._listenForBulkSuccess(event);
        this.listenForColumnPositionChange = (event) => this._listenForColumnPositionChange(event);
        this.listenForFiltersTemplateAddSuccess = (event) => this._listenForFiltersTemplateAddSuccess(event);
    }

    connect() {
        // if (!application.variables[this.componentKey]) {
            application.variables[this.componentKey] = {
                'type': 'specific',
                'items': new Set(),
                'except': new Set(),
            };
        // }

        const type = application.variables[this.componentKey].type;
        const specific = application.variables[this.componentKey].items;
        const except = application.variables[this.componentKey].except;

        this.bulkCheckboxTargets.forEach(element => {
            if (type === 'specific' && specific.has(element.dataset.entryId)) {
                element.checked = specific.has(element.dataset.entryId);
            }

            if (type === 'all' && !except.has(element.dataset.entryId)) {
                element.checked = true;
            }
        });

        this.checkIfAllToggled();
        application.element.addEventListener('dashboard-form:success', this.listenForBulkSuccess);
        application.element.addEventListener('dashboard-form:success', this.listenForFiltersTemplateAddSuccess);
        application.element.addEventListener('dnd:end', this.listenForColumnPositionChange);
    }

    disconnect() {
        application.element.removeEventListener('dashboard-form:success', this.listenForBulkSuccess);
        application.element.removeEventListener('dashboard-form:success', this.listenForFiltersTemplateAddSuccess);
        application.element.removeEventListener('dnd:end', this.listenForColumnPositionChange);
        super.disconnect();
    }

    tableTargetConnected() {
        window.addEventListener('scroll', this.listenForVerticalScroll);
    }

    tableTargetDisconnected() {
        window.removeEventListener('scroll', this.listenForVerticalScroll);
    }

    _listenForVerticalScroll() {
        if (this.hasTableTarget && this.scrollHandled) {
            this.scrollHandled = false;

            window.requestAnimationFrame(() => {
                const rect = this.tableTarget.getBoundingClientRect();
                const topOffset = rect.top + window.scrollY;

                if (window.scrollY > topOffset) {
                    this.tableTarget.firstElementChild.style.transform = 'translateY(' + (window.pageYOffset - topOffset) + 'px)';
                } else {
                    this.tableTarget.firstElementChild.style.transform = 'none';
                }

                this.scrollHandled = true;
            });
        }
    }

    _listenForBulkSuccess(event) {
        if (event?.detail?.source.classList.contains('dashboard-modal-form')) {
            application.variables[this.componentKey] = {
                'type': 'specific',
                'items': new Set(),
                'except': new Set(),
            };
        }
    }

    _listenForFiltersTemplateAddSuccess(event) {
        if (event?.detail?.source.classList.contains('dashboard-modal-save-filters-template-form')) {
            if (this.hasFilterTemplateSelectTarget) {
                const selectController = Stimulus.getControllerForElementAndIdentifier(this.filterTemplateSelectTarget, 'select-table-filter-template');

                if (selectController) {
                    selectController.reload();
                }
            }
            application.closeModals(1);
        }
    }

    _listenForColumnPositionChange(event) {
        const from = event?.detail?.from;
        const to = event?.detail?.to;
        const mode = event?.detail?.mode;

        if (from?.dataset.dragKey && to?.dataset.dragKey && mode) {
            this.columnTargets.forEach((fromColumn) => {
                if (fromColumn.dataset.columnKey === from.dataset.dragKey) {
                    const toColumn = fromColumn.parentElement.querySelector(`[data-column-key="${to.dataset.dragKey}"]`);

                    if (toColumn) {
                        if (mode === 'after') {
                            toColumn.after(fromColumn);
                        }

                        if (mode === 'before') {
                            toColumn.before(fromColumn);
                        }
                    }
                }
            });

            const wrapper = event.detail.from.closest('.column-preferences-items-wrapper');
            const items = wrapper.querySelectorAll('[data-toggle-key]');
            const keys = Array.prototype.map.call(items, (opt) => opt.dataset.toggleKey);

            fetch(dashboardAction('authorization', 'user', 'saveTableColumnOrderPreferences'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    _token: getMeta('csrf-token'),
                    model: this.element.dataset.modelClass,
                    order: keys,
                }),
            })
                .then(async response => {
                    if (response.status !== 200) {
                        if (response.headers.get('Content-Type') === 'application/json') {
                            application.toastError(await response.json());
                        } else {
                            application.toastGeneralError();
                        }
                    }
                })
                .catch((e) => {
                    console.error(e);
                    application.toastGeneralError();
                });
        }
    }

    saveFiltersTemplate() {
        const validParameters = {};

        for (const typeSelect of this.element.querySelectorAll('[data-filter-type-input]')) {
            const type = typeSelect.value;
            const typeKey = typeSelect.dataset.filterTypeInput;
            const value = this.element.querySelector('[data-filter-value-input="' + typeKey + '"]');

            if (value && value.value.trim() !== '') {
                if (value.nodeName.toLowerCase() === 'select') {
                    const selectedOptions = [];

                    for (const option of value.selectedOptions) {
                        selectedOptions.push({
                            label: option.innerHTML,
                            value: option.value,
                        });
                    }

                    validParameters[typeKey] = {
                        inputType: 'select',
                        type: type,
                        value: selectedOptions,
                    };
                } else {
                    validParameters[typeKey] = {
                        inputType: 'raw',
                        type: type,
                        value: value.value,
                    };
                }
            }
        }

        fetch(dashboardAction('settings', 'table-filter-template', 'saveTableFiltersTemplate'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                _token: getMeta('csrf-token'),
                template_model: this.element.dataset.modelClass,
                template_query: JSON.stringify(validParameters),
            }),
        })
            .then(async response => {
                if (response.status === 200) {
                    application.createModalOfContent(await response.text(), null);
                } else {
                    application.toastError(await response.json());
                }
            })
            .catch((e) => {
                console.error(e);
                application.toastGeneralError();
            });
    }

    selectFilters(event) {
        event.preventDefault();

        const allFilterParams = this.collectFilterParams();
        const urlSearchParams = new URLSearchParams(window.location.search);

        for (const typeSelect of this.element.querySelectorAll('[data-filter-type-input]')) {
            const typeKey = '__' + typeSelect.dataset.filterTypeInput;
            urlSearchParams.delete(typeKey);
        }

        for (const key in allFilterParams) {
            urlSearchParams.set(key, allFilterParams[key]);
        }

        urlSearchParams.delete('page');

        Turbo.visit(location.pathname + '?' + urlSearchParams);
    }

    presetFiltersFromQuery(query) {
        Object.entries(query).forEach(([key, data]) => {
            const typeFilter = this.element.querySelector(`[data-filter-type-input="${key}"]`);
            const valueFilter = this.element.querySelector(`[data-filter-value-input="${key}"]`);

            if (typeFilter && valueFilter && data.type && data.value) {
                if (data.inputType === 'raw' && valueFilter.nodeName.toLowerCase() !== 'select') {
                    typeFilter.value = data.type;
                    valueFilter.value = data.value;

                    typeFilter.dispatchEvent(new CustomEvent('input', {
                        bubbles: false,
                        cancelable: true,
                    }));
                }

                if (data.inputType === 'select' && valueFilter.nodeName.toLowerCase() === 'select') {
                    const select = valueFilter.closest('[data-controller="select"]');

                    if (select) {
                        const selectController = Stimulus.getControllerForElementAndIdentifier(select, 'select');

                        if (selectController) {
                            selectController.select.clear();
                            selectController.select.clearOptions();
                            selectController.reload();
                            typeFilter.value = data.type;

                            typeFilter.dispatchEvent(new CustomEvent('input', {
                                bubbles: false,
                                cancelable: true,
                            }));

                            for (const value of data.value) {
                                const option = {};

                                option[selectController.optionsValue] = value.value;
                                option[selectController.optionsLabel] = value.label;

                                selectController.select.addOption(option);
                                selectController.select.addItem(value.value);
                            }
                        }
                    }
                }
            }
        });
    }

    clear() {
        const urlSearchParams = new URLSearchParams(window.location.search);

        for (const typeSelect of this.element.querySelectorAll('[data-filter-type-input]')) {
            const typeKey = '__' + typeSelect.dataset.filterTypeInput;
            urlSearchParams.delete(typeKey);
        }

        urlSearchParams.delete('page');

        Turbo.visit(location.pathname + '?' + urlSearchParams);
    }

    collectFilterParams() {
        const validParameters = {};

        for (const typeSelect of this.element.querySelectorAll('[data-filter-type-input]')) {
            const type = typeSelect.value;
            const typeKey = typeSelect.dataset.filterTypeInput;
            const value = this.element.querySelector('[data-filter-value-input="' + typeKey + '"]');

            if (value && value.value.trim() !== '') {
                if (value.nodeName.toLowerCase() === 'select' && value.hasAttribute('multiple')) {
                    const selectedOptions = Array.prototype.map.call(value.selectedOptions, (opt) => opt.value);
                    validParameters['__' + typeKey] = type + '_' + selectedOptions.join(',');
                } else {
                    validParameters['__' + typeKey] = type + '_' + value.value;
                }
            }
        }

        return validParameters;
    }

    toggleAllColumns(event) {
        const checked = event.currentTarget.checked;

        for (const input of this.visibilityInputTargets) {
            input.checked = checked;
        }

        this.toggleColumn();
    }

    toggleColumn() {
        const update = {};
        let allChecked = true;

        for (const input of this.visibilityInputTargets) {
            const toggleKey = input.dataset.toggleKey;
            update[toggleKey] = input.checked;

            if (!input.checked) {
                allChecked = false;
            }

            this.columnTargets.forEach(el => {
                if (toggleKey === el.dataset.columnKey) {
                    if (input.checked) {
                        el.classList.remove('d-none');
                    } else {
                        el.classList.add('d-none');
                    }
                }
            });
        }

        this.visibilityInputAllTarget.checked = allChecked;

        fetch(dashboardAction('authorization', 'user', 'saveTableColumnVisibilityPreferences'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                _token: getMeta('csrf-token'),
                model: this.element.dataset.modelClass,
                update: update,
            }),
        })
            .then(async response => {
                if (response.status !== 200) {
                    if (response.headers.get('Content-Type') === 'application/json') {
                        application.toastError(await response.json());
                    } else {
                        application.toastGeneralError();
                    }
                }
            })
            .catch((e) => {
                console.error(e);
                application.toastGeneralError();
            });
    }

    savePreferredCurrency(event) {
        const selectedCurrency = event.target.value;

        fetch(dashboardAction('authorization', 'user', 'saveCurrencyPreferences'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                _token: getMeta('csrf-token'),
                model: this.element.dataset.modelClass,
                currency: selectedCurrency,
            }),
        })
            .then(async response => {
                if (response.status !== 200) {
                    if (response.headers.get('Content-Type') === 'application/json') {
                        application.toastError(await response.json());
                    } else {
                        application.toastGeneralError();
                    }
                }
                Turbo.visit('');
            })
            .catch((e) => {
                console.error(e);
                application.toastGeneralError();
            });
    }

    checkIfAllToggled() {
        const type = application.variables[this.componentKey].type;
        const except = application.variables[this.componentKey].except;
        const specific = application.variables[this.componentKey].items;
        let allChecked = this.bulkCheckboxTargets.length > 0;

        for (const element of this.bulkCheckboxTargets) {
            if (!element.checked) {
                allChecked = false;
                break;
            }
        }

        this.bulkCheckboxAllTargets.forEach(element => {
            element.checked = allChecked;
        });

        if (type === 'specific') {
            this.totalBulkCount = specific.size;
        }

        if (type === 'all') {
            this.totalBulkCount = parseInt(this.element.dataset.total) - except.size;
        }

        this.bulkActionDropdownTargets.forEach(element => {
            if (this.totalBulkCount > 0) {
                element.classList.remove('disabled');
            } else {
                element.classList.add('disabled');
            }
        });

        this.bulkCountBadgeTargets.forEach(element => {
            element.innerHTML = this.totalBulkCount;

            if (this.totalBulkCount > 0) {
                element.classList.remove('d-none');
            } else {
                element.classList.add('d-none');
            }
        });

        if (this.totalBulkCount > 0) {
            this.bulkActionLinkTargets.forEach(element => {
                const originalLink = element.dataset.originalHref;
                const params = new URLSearchParams(window.location.search);

                params.set('bulk_type', application.variables[this.componentKey].type);
                params.set('bulk_count', this.totalBulkCount);

                let index = 0;
                application.variables[this.componentKey].items.forEach(item => {
                    params.append(`bulk_items[${index++}]`, item.toString());
                });

                index = 0;
                application.variables[this.componentKey].except.forEach(item => {
                    params.append(`bulk_except[${index++}]`, item.toString());
                });

                element.href = originalLink + '?' + params;
            });
        } else {
            this.bulkActionLinkTargets.forEach(element => {
                element.href = '#';
            });
        }
    }

    bulkSelectSingle(event) {
        if (this.bulkCheckboxTargets.length <= 0) {
            application.variables[this.componentKey].type = 'specific';
            this.checkIfAllToggled();
            return;
        }

        if (application.variables[this.componentKey].type === 'specific') {
            if (event.target.checked) {
                application.variables[this.componentKey].items.add(event.target.dataset.entryId);
            } else {
                application.variables[this.componentKey].items.delete(event.target.dataset.entryId);
            }
        } else if (application.variables[this.componentKey].type === 'all') {
            if (event.target.checked) {
                application.variables[this.componentKey].except.delete(event.target.dataset.entryId);
            } else {
                application.variables[this.componentKey].except.add(event.target.dataset.entryId);
            }
        }

        this.checkIfAllToggled();
    }

    bulkSelectPage() {
        if (this.bulkCheckboxTargets.length <= 0) {
            application.variables[this.componentKey].type = 'specific';
            this.checkIfAllToggled();
            return;
        }

        application.variables[this.componentKey].type = 'specific';
        application.variables[this.componentKey].except.clear();

        this.bulkCheckboxTargets.forEach(element => {
            element.checked = true;
            application.variables[this.componentKey].items.add(element.dataset.entryId);
        });

        this.checkIfAllToggled();
    }

    bulkDeselectPage() {
        if (this.bulkCheckboxTargets.length <= 0) {
            application.variables[this.componentKey].type = 'specific';
            this.checkIfAllToggled();
            return;
        }

        if (application.variables[this.componentKey].type === 'all') {
            this.bulkCheckboxTargets.forEach(element => {
                element.checked = false;
                application.variables[this.componentKey].except.add(element.dataset.entryId);
            });
        } else {
            application.variables[this.componentKey].except.clear();

            this.bulkCheckboxTargets.forEach(element => {
                element.checked = false;
                application.variables[this.componentKey].items.delete(element.dataset.entryId);
            });
        }

        this.checkIfAllToggled();
    }

    bulkSelectAll() {
        if (this.bulkCheckboxTargets.length <= 0) {
            application.variables[this.componentKey].type = 'specific';
            this.checkIfAllToggled();
            return;
        }

        application.variables[this.componentKey].type = 'all';

        this.bulkCheckboxTargets.forEach(element => {
            element.checked = true;
        });

        application.variables[this.componentKey].items.clear();

        this.checkIfAllToggled();
    }

    bulkDeselectAll() {
        if (this.bulkCheckboxTargets.length <= 0) {
            application.variables[this.componentKey].type = 'specific';
            this.checkIfAllToggled();
            return;
        }

        application.variables[this.componentKey].type = 'specific';

        this.bulkCheckboxTargets.forEach(element => {
            element.checked = false;
        });

        application.variables[this.componentKey].items.clear();
        application.variables[this.componentKey].except.clear();

        this.checkIfAllToggled();
    }

    updatePerPageQuery(event) {
        clearTimeout(this.perPageTypeTimer);

        this.perPageTypeTimer = setTimeout(() => {
            const perPage = parseInt(event.target.value);

            if (perPage > 0) {
                const urlSearchParams = new URLSearchParams(window.location.search);
                urlSearchParams.set('perPage', perPage.toString());
                urlSearchParams.delete('page');
                Turbo.visit(location.pathname + '?' + urlSearchParams);
            }
        }, 500);
    }
}
